import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ShieldCheck, ShieldAlert, ShieldX, Calendar, Package, MessageCircle,
    FileText, AlertTriangle, Cog, Hash, Factory,
} from "lucide-react";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import { useSetting } from "@/context/CmsContext";
import useSeo from "@/lib/useSeo";

/* Mobile-first public passport page reached via QR code scan.
   Privacy-safe: no owner info, no costs, no supplier data, no full component serials. */
export default function UnitPassport() {
    const { serial } = useParams();
    const { tr, en } = useLocale();
    const whatsapp = useSetting("whatsapp_number", "351900000000");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useSeo({
        title: data ? `${data.serial_number} — NOVIFOOD` : "NOVIFOOD Unit",
        description: data
            ? `${data.commercial_model || data.serial_number} · ${data.production_year || ""}`
            : "NOVIFOOD unit passport",
        path: `/unit/${serial || ""}`,
    });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                const { data: d } = await api.get(`/unit/${encodeURIComponent(serial)}/passport`);
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [serial]);

    if (loading) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center">
                <div className="animate-pulse text-sm text-muted-foreground">A carregar…</div>
            </div>
        );
    }

    if (error === "not_found") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-4">
                <div className="max-w-md text-center">
                    <AlertTriangle className="mx-auto h-10 w-10 text-secondary" />
                    <h1 className="mt-4 font-heading text-2xl font-extrabold text-primary">
                        {tr("Unidade não encontrada", "Unit not found")}
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        {tr(
                            `O identificador "${serial}" não corresponde a nenhuma unidade NOVIFOOD.`,
                            `Identifier "${serial}" does not match any NOVIFOOD unit.`
                        )}
                    </p>
                    <Link to="/" className="mt-6 inline-flex text-sm font-bold text-secondary hover:underline">
                        {tr("Ir para novifood.pt", "Go to novifood.pt")}
                    </Link>
                </div>
            </div>
        );
    }

    if (error === "error" || !data) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-4">
                <p className="text-sm text-red-500">
                    {tr("Erro a carregar a unidade. Tente novamente.", "Error loading the unit. Please try again.")}
                </p>
            </div>
        );
    }

    // Warranty visual
    const w = data.warranty || {};
    const WIcon = w.status === "active" ? ShieldCheck : w.status === "expiring" ? ShieldAlert : ShieldX;
    const wColor = {
        active: { bg: "bg-emerald-500", text: "text-emerald-600", tint: "bg-emerald-50 border-emerald-200" },
        expiring: { bg: "bg-amber-500", text: "text-amber-600", tint: "bg-amber-50 border-amber-200" },
        expired: { bg: "bg-red-500", text: "text-red-600", tint: "bg-red-50 border-red-200" },
        unknown: { bg: "bg-gray-500", text: "text-gray-600", tint: "bg-gray-50 border-gray-200" },
    }[w.status || "unknown"];
    const wLabel = en ? w.label_en : w.label_pt;

    return (
        <div className="min-h-screen bg-gradient-to-b from-primary/[0.03] to-transparent">
            <div className="mx-auto max-w-md px-4 py-6 lg:py-10">
                {/* Brand header */}
                <div className="text-center mb-6">
                    <div className="font-heading text-xl font-black tracking-widest text-primary">NOVIFOOD</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">
                        {tr("Passaporte da Unidade", "Unit Passport")}
                    </div>
                </div>

                {/* Reported lost banner */}
                {data.reported_lost && (
                    <div className="mb-4 rounded-xl border-2 border-red-500 bg-red-50 p-4 text-center" data-testid="unit-lost-banner">
                        <AlertTriangle className="mx-auto h-6 w-6 text-red-600" />
                        <div className="mt-1 font-heading font-extrabold text-red-700 uppercase tracking-wider text-sm">
                            {tr("Reportado como perdido/roubado", "Reported lost/stolen")}
                        </div>
                        <a
                            href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Encontrei o carrinho NOVIFOOD ${data.serial_number}`)}`}
                            target="_blank" rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-xs font-bold text-white"
                        >
                            {tr("Reportar que encontrei", "Report that I found it")}
                        </a>
                    </div>
                )}

                {/* Hero card */}
                <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden">
                    {data.photo_url ? (
                        <div className="aspect-[4/3] overflow-hidden bg-muted">
                            <img src={data.photo_url} alt="" className="w-full h-full object-cover" />
                        </div>
                    ) : (
                        <div className="aspect-[4/3] flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
                            <Package className="h-20 w-20 text-primary/30" strokeWidth={1} />
                        </div>
                    )}
                    <div className="p-5">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-bold">
                            {tr("Modelo", "Model")}
                        </div>
                        <h1 className="mt-1 font-heading text-2xl font-extrabold text-primary leading-tight" data-testid="unit-model">
                            {data.commercial_model || data.model || "NOVIFOOD"}
                        </h1>
                        {data.technical_code && (
                            <div className="text-xs text-muted-foreground font-mono mt-1">{data.technical_code}</div>
                        )}

                        <hr className="my-4 border-border" />

                        <dl className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <dt className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
                                    <Hash className="h-3 w-3" /> {tr("Unidade", "Unit")}
                                </dt>
                                <dd className="mt-0.5 font-mono font-bold text-primary" data-testid="unit-serial">
                                    {data.serial_number}
                                </dd>
                            </div>
                            {data.production_year && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
                                        <Factory className="h-3 w-3" /> {tr("Fabricado", "Made in")}
                                    </dt>
                                    <dd className="mt-0.5 font-bold text-primary" data-testid="unit-year">
                                        {data.production_year}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>

                {/* Warranty card */}
                <div className={`mt-4 rounded-2xl border p-5 ${wColor.tint}`} data-testid="unit-warranty">
                    <div className="flex items-center gap-3">
                        <div className={`h-12 w-12 rounded-xl ${wColor.bg} flex items-center justify-center shadow-sm`}>
                            <WIcon className="h-6 w-6 text-white" strokeWidth={2.2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-primary/70">
                                {tr("Estado da garantia", "Warranty status")}
                            </div>
                            <div className={`font-heading text-lg font-extrabold ${wColor.text}`}>
                                {wLabel || "—"}
                            </div>
                            {w.until && (
                                <div className="text-xs text-primary/70 mt-0.5 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {tr("Válida até", "Valid until")} {w.until}
                                    {w.status === "active" && w.days_remaining !== null && (
                                        <span className="text-primary/50">· {w.days_remaining} {tr("dias", "days")}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Components (public — name + category only) */}
                {data.components_public && data.components_public.length > 0 && (
                    <div className="mt-4 rounded-2xl bg-white border border-border p-5">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-primary/70 flex items-center gap-1">
                            <Cog className="h-3 w-3" /> {tr("Configuração", "Configuration")}
                        </div>
                        <ul className="mt-3 divide-y divide-border">
                            {data.components_public.map((p, i) => (
                                <li key={i} className="py-2 flex items-center gap-3">
                                    <Package className="h-4 w-4 text-primary/50 shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold text-primary truncate">{p.name}</div>
                                        {(p.manufacturer || p.category) && (
                                            <div className="text-[11px] text-muted-foreground truncate">
                                                {[p.manufacturer, p.category].filter(Boolean).join(" · ")}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Action buttons — sticky on mobile bottom-ish */}
                <div className="mt-5 grid grid-cols-2 gap-3">
                    <a
                        href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Assistência para NOVIFOOD ${data.serial_number}`)}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-white text-sm font-bold py-3 shadow-sm hover:bg-primary/90 transition"
                        data-testid="unit-action-service"
                    >
                        <MessageCircle className="h-4 w-4" />
                        {tr("Pedir assistência", "Request service")}
                    </a>
                    <Link
                        to="/contacto"
                        className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-primary text-primary text-sm font-bold py-3 hover:bg-primary/5 transition"
                        data-testid="unit-action-contact"
                    >
                        <FileText className="h-4 w-4" />
                        {tr("Contactar NOVIFOOD", "Contact us")}
                    </Link>
                </div>

                {/* Owner login hint */}
                <p className="mt-4 text-center text-[11px] text-muted-foreground leading-relaxed">
                    {tr(
                        "Se é o proprietário desta unidade, ",
                        "If you own this unit, "
                    )}
                    <Link to="/entrar" className="font-bold text-primary underline">
                        {tr("faça login", "sign in")}
                    </Link>
                    {tr(
                        " na sua conta para aceder a documentação completa, histórico e pedidos de assistência.",
                        " to access full documentation, history and service requests."
                    )}
                </p>

                {/* Footer strip */}
                <div className="mt-8 pt-6 border-t border-border text-center text-[11px] text-muted-foreground">
                    <div>
                        <Link to="/" className="hover:text-primary transition">novifood.pt</Link>
                        {" · "}
                        <span className="font-mono">{data.serial_number}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
